// The puzzle card's words and its green flash, decided from the solver's
// state: pure, so they are tested without React. The container in
// `index.tsx` passes these to `PuzzleCard` and runs the flash's timer.
import { describePrize, plyLabel } from './finder.ts';
import type { PuzzleCardStatus } from './card-tone.ts';
import type { Solving } from './solving.ts';

/** The card's props drawn from the solver: everything but its callbacks and the flash. */
export interface CardText {
  status: PuzzleCardStatus;
  title: string;
  prompt: string;
  progress: string;
  text: string;
}

/** What the puzzle asks, where the reader is in it and the state line. */
export function cardOf(solving: Solving): CardText {
  const p = solving.puzzle;
  const title = p.kind === 'theory' ? 'Complete the opening' : plyLabel(p.ply);
  const prompt = p.kind === 'tactic' || p.kind === 'theory' ? describePrize(p.prize) : 'Find the better move';
  const step = p.kind === 'theory' ? `move ${Math.min((solving.step ?? 0) + 1, solving.needed)} of ${solving.needed} · ` : '';
  const text =
    solving.status === 'playing'
      ? p.kind === 'theory'
        ? p.prize.kind === 'theory' && p.prize.steps[solving.step ?? 0]?.source === 'engine'
          ? 'The theory is over here. Play the best move.'
          : 'Play your next move of the theory on the board.'
        : solving.applied === 0
          ? `${p.side === 'white' ? 'White' : 'Black'} to move. Play it on the board.`
          : 'Your move.'
      : solving.status === 'thinking'
        ? 'The engine is answering…'
        : solving.status === 'solved'
          ? solving.playOut?.playing
            ? playingOutLine(solving)
            : 'Solved. The line is yours.'
          : solving.status === 'showing'
            ? solving.solution?.length
              ? `Solution: ${solving.solution.join(' ')} …`
              : 'Playing the solution…'
            : solving.status === 'shown'
              ? solving.solution?.length
                ? `The solution: ${solving.solution.join(' ')}. Try it yourself.`
                : 'That is the solution. Try it yourself.'
              : 'Not that move. Taking it back…';
  return { status: solving.status, title, prompt, progress: `${step}try ${solving.tries}`, text };
}

/** "Solved · Nh5 — playing it out: Qxh5 Bxf7+ …": the reader's moves, then the line as it plays out after them. */
export function playingOutLine(solving: Solving): string {
  const sans = solving.solution ?? [];
  const from = solving.playOut?.from ?? sans.length;
  const found = sans.slice(0, from).join(' ');
  const rest = sans.slice(from).join(' ');
  return `Solved · ${found} — playing it out: ${rest ? `${rest} ` : ''}…`;
}

/**
 * Whether every click is blocked (2026-10-12): from the solving move until
 * the line has fully played out, so nothing interrupts it; the shield in
 * puzzles.css covers the page meanwhile. Not while the solution is shown on
 * request (the reader asked for it and may stop it), nor once the play-out
 * is over.
 */
export function clicksBlocked(solving: Solving | undefined): boolean {
  if (!solving || solving.status !== 'solved') return false;
  if (solving.playOut === undefined) return solving.solvedMove !== undefined && !solving.playedOut;
  return solving.playOut.playing;
}

/** The card's prompt for the constraints' detail: its title and what it asks. */
export function promptOf(solving: Solving | undefined): string | undefined {
  if (!solving) return undefined;
  const card = cardOf(solving);
  return `${card.title} ${card.prompt}`;
}

/** How long the card stays green after a correct step. */
export const CORRECT_FLASH_MS = 1000;

/** What the flash remembers of the solver between states. */
export interface FlashMemory {
  id?: string;
  applied: number;
}

/**
 * Whether the card flashes green: a reader move of the same puzzle was
 * accepted (`applied` grew) and the puzzle is still going. Not a failed or
 * solved state (solved is green on its own), and not the solution playing
 * itself.
 */
export function flashes(before: FlashMemory, solving: Solving): boolean {
  const grew = before.id === solving.puzzle.id && solving.applied > before.applied;
  return grew && solving.status === 'playing';
}

/** What the flash remembers after a state. */
export function memoryOf(solving: Solving): FlashMemory {
  return { id: solving.puzzle.id, applied: solving.applied };
}
